/* eslint-disable @typescript-eslint/no-explicit-any */

import axios from 'axios'
import packageJson from '../../package.json'
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import * as Sentry from '@sentry/vue'
import * as services from './api'

interface AxiosHeader {
  headerKey: string
  headerValue: string
}
interface Services {
  [key: string]: any
}

// Set Api
const setClientApi = (clientApiV1: Services, axios: AxiosInstance) => {
  const clientApi = {} as Services

  Object.keys(clientApiV1).forEach((service: string) => {
    clientApi[service] = clientApiV1[service](axios)
  })

  const setHeader = (headers: AxiosHeader[]) => {
    axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*'

    headers.forEach((header: AxiosHeader) => {
      axios.defaults.headers[header.headerKey] = header.headerValue
    })
  }

  return {
    axiosConfig: axios.defaults,
    client: clientApi,
    setHeader,
  }
}

const setAxios = () => {
  const BASE_URL: string = import.meta.env.VITE_API_BASEURL as string
  const queryParams = new URLSearchParams(window.location.search)
  const jwt = queryParams.get('token') as string

  const api = axios.create({
    baseURL: BASE_URL,
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
      'X-App-Version': packageJson.version,
      'X-App-Platform': 'game-contest',
    },
  }) as AxiosInstance

  api.interceptors.response.use(
    function (response: AxiosResponse) {
      const res = {
        ...response.data,
        headers: response.headers,
      } as AxiosResponse
      return res
    },
    function (error: AxiosError) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        },
      })

      return Promise.reject(error)
    },
  )

  api.interceptors.request.use(
    function (config: InternalAxiosRequestConfig) {
      const requestId = crypto.randomUUID()
      config.headers['X-Request-ID'] = requestId

      return config
    },
    function (error: AxiosError) {
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          data: error.response?.data,
        },
      })

      return Promise.reject(error)
    },
  )
  return api
}

const API = setAxios()
const clientApi = setClientApi(services.default, API)

export { API, clientApi }
